import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash-es'
import OverviewDetailItems from './OverviewDetailItems'
import OverviewAbout from './OverviewAbout'
import ContactButton from './ContactButton'
import { SocialLink } from 'components/SocialLink'
import TrackedLink from './TrackedLink'
import { IconVariant, SupplierIcon } from 'components//supplier_shared/supplier_icon'
import { StarRating } from 'components/review_ratings'
import { ReviewCount } from 'components/common/rating'

const Overview = ({
  supplier, supplier: {
    name,
    address,
    phone,
    website,
    contactMethod,
    pro,
    id,
    rating,
    reviewCount,
    logoUrl,
    socialMedia
  }
}) => {
  const [displayPhone, setDisplayPhone] = useState(false)

  const renderAddress = () => {
    if (address) {
      return (
        <div className='shrink cell bottom-spaced'>
          <i className='far fa-map-marker color-pro-blue-500' /> {address}
        </div>
      )
    }
  }

  const renderPhone = () => {
    const handlePhoneClick = () => setDisplayPhone(true)

    if (phone) {
      return (
        <div className='shrink cell'>
          {displayPhone
            ? (
              <a
                href={`tel:${phone}`}
                className='overview-link'
              >
                <i className='far fa-phone color-pro-blue-500 contact-icon' />{phone}
              </a>
              )
            : (
              <TrackedLink
                supplierId={id}
                eventType='supplier_phone_link'
                additionalHandleClick={handlePhoneClick}
                linkClasses='overview-link'
                key='phone-link'
              >
                <i className='far fa-phone color-pro-blue-500 contact-icon' />View Phone Number
              </TrackedLink>
              )}
        </div>
      )
    }
  }

  const renderWebsite = () => {
    if (website) {
      return (
        <div className='shrink cell'>
          <TrackedLink
            url={website}
            supplierId={id}
            eventType='supplier_website_link'
            linkClasses='overview-link'
            key='web-link'
          >
            <i className='far fa-link color-pro-blue-500 contact-icon' />Website
          </TrackedLink>
        </div>
      )
    }
  }

  const renderSocial = () => {
    if (!(isEmpty(socialMedia))) {
      return (
        <div className='shrink cell'>
          {socialMedia.map(platform => renderSocialLink(platform))}
        </div>
      )
    }
  }

  const renderSocialLink = ({ link, platform }) => (
    <TrackedLink
      url={link}
      supplierId={id}
      eventType={`supplier_social_links_${platform}`}
      linkClasses='social-link'
      key={`social-link-${platform}`}
    >
      <SocialLink platform={platform} />
    </TrackedLink>
  )

  const renderStarRating = () => {
    return (
      <div className='shrink cell overview-rating'>
        <ReviewCount reviewCount={reviewCount} url='#reviews' noReviewContent={<></>}>
          <StarRating rating={rating} />
        </ReviewCount>
      </div>
    )
  }

  const renderContactButton = () => {
    if (pro && contactMethod.contactConfigured) {
      return (
        <div className='shrink cell'>
          <ContactButton
            pro={pro}
            contactByEmail={contactMethod.contactByEmail}
            link={contactMethod.link}
            supplierId={id}
            contactConfigured={contactMethod.contactConfigured}
            trackingKey='supplier_overview_contact_button'
          />
        </div>
      )
    }
  }

  return (
    <>
      <div className='pro-bordered padding-3'>
        <div className='grid-x grid-margin-x pro-padded'>
          <div className='medium-shrink small-12 cell small-top-padded'>
            <SupplierIcon variant={IconVariant.XLARGE} id={id} name={name} logoUrl={logoUrl} />
          </div>
          <div className='medium-auto small-12 cell'>
            <h1 className='color-pro-steel-grey-600 pro-head-3 word-wrap'>{name}</h1>
            {renderAddress()}
            <div className='grid-x grid-margin-x bottom-spaced'>
              {renderPhone()}
              {renderWebsite()}
              {renderSocial()}
            </div>
            <div className='grid-x grid-margin-x'>
              {renderContactButton()}
              {renderStarRating()}
            </div>
          </div>
        </div>
      </div>
      <OverviewAbout
        supplier={supplier}
      />
      <OverviewDetailItems
        supplier={supplier}
      />
    </>
  )
}

Overview.propTypes = {
  supplier: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    phone: PropTypes.string,
    website: PropTypes.string,
    contactMethod: PropTypes.shape({
      contactConfigured: PropTypes.bool,
      contactByEmail: PropTypes.bool,
      link: PropTypes.string
    }),
    pro: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rating: PropTypes.number,
    reviewCount: PropTypes.number,
    logoUrl: PropTypes.string,
    socialMedia: PropTypes.arrayOf(PropTypes.shape({
      link: PropTypes.string,
      platform: PropTypes.string
    }))
  }).isRequired
}

export default Overview
